import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PlusCircle, FileText, BarChart3, Trophy, Edit, Trash2, Image as ImageIcon, Calendar, Lock, Eye, EyeOff, RefreshCw, Copy, Check, Settings, Database, FileCode, LayoutGrid, Home, Star, GripVertical, Play, ChevronRight, MessageSquare } from 'lucide-react';
import CommunityCommentsManager from '../components/CommunityCommentsManager';
import SiteHealthBanner from '../components/SiteHealthBanner';
import { QBAwardsAdmin } from '../components/qb/QBAwardsAdmin';
import { QBPickemLinker } from '../components/qb/QBPickemLinker';
import { toast } from 'sonner';
import RichMediaToolbar from '../components/RichMediaToolbar';
import InteractiveHtmlUploader from '../components/InteractiveHtmlUploader';
import InteractiveArticlesList from '../components/InteractiveArticlesList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('articles');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check if already authenticated in session
  useEffect(() => {
    const authStatus = sessionStorage.getItem('adminAuth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('adminAuth', 'true');
        sessionStorage.setItem('adminPassword', password);
        toast.success('Welcome to Admin Dashboard!');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Incorrect password. Please try again.';
      setLoginError(errorMsg);
      toast.error('Incorrect password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
    sessionStorage.removeItem('adminPassword');
    setPassword('');
    toast.info('Logged out of admin panel');
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="admin-login">
        <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md">
          <div className="text-center mb-8">
            <div className="bg-[#001f3f] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold uppercase heading-font text-[#001f3f]">Admin Access</h1>
            <p className="text-slate-600 mt-2">Enter password to continue</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-4 pr-12 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-[#007AFF] focus:border-transparent text-lg"
                  placeholder="Enter admin password"
                  required
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {loginError && (
                <p className="text-red-600 text-sm mt-2">{loginError}</p>
              )}
            </div>
            
            <Button
              type="submit"
              className="w-full bg-[#DC2626] hover:bg-[#b91c1c] text-white px-8 py-4 rounded-full font-bold uppercase tracking-wider text-lg disabled:opacity-50"
              data-testid="admin-login-btn"
              disabled={isLoading}
            >
              <Lock className="h-5 w-5 mr-2" />
              {isLoading ? 'Verifying...' : 'Access Admin Panel'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <div className="bg-[#001f3f] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight uppercase heading-font" data-testid="admin-title">
              Admin Dashboard
            </h1>
            <p className="text-slate-300 mt-2">Content Management System</p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-white text-white hover:bg-white hover:text-[#001f3f]"
            data-testid="admin-logout-btn"
          >
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SiteHealthBanner />
        <ElectionModeManager />
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-9 max-w-5xl mb-8">
            <TabsTrigger value="articles" data-testid="tab-articles">
              <FileText className="h-4 w-4 mr-2" />
              Articles
            </TabsTrigger>
            <TabsTrigger value="new-article" data-testid="tab-new-article">
              <PlusCircle className="h-4 w-4 mr-2" />
              New Article
            </TabsTrigger>
            <TabsTrigger value="interactive" data-testid="tab-interactive">
              <LayoutGrid className="h-4 w-4 mr-2" />
              Block Editor
            </TabsTrigger>
            <TabsTrigger value="homepage" data-testid="tab-homepage">
              <Home className="h-4 w-4 mr-2" />
              Homepage
            </TabsTrigger>
            <TabsTrigger value="comments" data-testid="tab-comments">
              <MessageSquare className="h-4 w-4 mr-2" />
              Comments
            </TabsTrigger>
            <TabsTrigger value="qb-club" data-testid="tab-qb-club">
              <Trophy className="h-4 w-4 mr-2" />
              QB Club
            </TabsTrigger>
            <TabsTrigger value="schedule" data-testid="tab-schedule">
              <Calendar className="h-4 w-4 mr-2" />
              Schedule
            </TabsTrigger>
            <TabsTrigger value="poll" data-testid="tab-poll">
              <BarChart3 className="h-4 w-4 mr-2" />
              New Poll
            </TabsTrigger>
            <TabsTrigger value="prediction" data-testid="tab-prediction">
              <Trophy className="h-4 w-4 mr-2" />
              Prediction
            </TabsTrigger>
            <TabsTrigger value="tools" data-testid="tab-tools">
              <Settings className="h-4 w-4 mr-2" />
              Tools
            </TabsTrigger>
          </TabsList>

          <TabsContent value="articles">
            <ArticlesList onEdit={() => setActiveTab('new-article')} />
          </TabsContent>

          <TabsContent value="new-article">
            <ArticleForm />
          </TabsContent>

          <TabsContent value="interactive">
            <InteractiveArticlesList />
          </TabsContent>

          <TabsContent value="homepage">
            <GOTWVideoManager />
            <div className="mt-6">
              <ScoreboardSettingsManager />
            </div>
            <div className="mt-6">
              <HomepageLayoutManager />
            </div>
          </TabsContent>

          <TabsContent value="comments">
            <CommunityCommentsManager />
          </TabsContent>

          <TabsContent value="qb-club">
            <QBClubManager />
          </TabsContent>

          <TabsContent value="schedule">
            <ScheduleManager />
          </TabsContent>

          <TabsContent value="poll">
            <PollForm />
          </TabsContent>

          <TabsContent value="prediction">
            <PredictionSection />
          </TabsContent>

          <TabsContent value="tools">
            <AdminTools />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const ArticlesList = ({ onEdit }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles?limit=50`);
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      await axios.delete(`${API}/articles/${articleId}`);
      toast.success('Article deleted');
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Failed to delete article');
    }
  };

  const syncNewArticles = async () => {
    try {
      setSyncing(true);
      const pwd = sessionStorage.getItem('adminPassword') || 'Braxton0604!!!';
      const response = await axios.get(`${API}/admin/sync-new-articles`, {
        params: { password: pwd }
      });
      const { added, skipped } = response.data;
      if (added > 0) {
        toast.success(`Synced ${added} new article(s)! ${skipped} already existed.`);
      } else {
        toast.info('All articles are already synced.');
      }
      fetchArticles();
    } catch (error) {
      console.error('Error syncing articles:', error);
      toast.error('Failed to sync articles');
    } finally {
      setSyncing(false);
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
  };

  const handleCancelEdit = () => {
    setEditingArticle(null);
  };

  const handleUpdateSuccess = () => {
    setEditingArticle(null);
    fetchArticles();
  };

  if (loading) {
    return <div className="text-center py-12">Loading articles...</div>;
  }

  if (editingArticle) {
    return <ArticleForm article={editingArticle} onSuccess={handleUpdateSuccess} onCancel={handleCancelEdit} />;
  }

  return (
    <div className="bg-white p-8 rounded-lg shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold uppercase heading-font text-[#001f3f]">Manage Articles</h2>
        <Button
          onClick={syncNewArticles}
          disabled={syncing}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="sync-articles-btn"
        >
          {syncing ? 'Syncing...' : 'Sync New Articles'}
        </Button>
      </div>
      
      {articles.length === 0 ? (
        <p className="text-center text-slate-600 py-8">No articles yet. Create your first one!</p>
      ) : (
        <div className="space-y-4">
          {articles.map(article => (
            <div key={article.article_id} className="border border-slate-200 p-4 rounded-lg flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-bold text-[#001f3f] mb-1">{article.title}</h3>
                <div className="flex items-center space-x-4 text-sm text-slate-600">
                  <span className="text-[#007AFF] font-semibold">{article.category}</span>
                  <span>{new Date(article.created_at).toLocaleDateString()}</span>
                  <span className="flex items-center gap-1">
                    <Eye className="h-3 w-3" />
                    {article.view_count || 0} views
                  </span>
                  {article.is_premium && (
                    <span className="bg-[#DC2626] text-[#001f3f] px-2 py-1 rounded text-xs font-bold">PREMIUM</span>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => handleEdit(article)}
                  variant="ghost"
                  size="sm"
                  className="text-[#007AFF] hover:text-[#005bb5] hover:bg-blue-50"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  onClick={() => handleDelete(article.article_id)}
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ArticleForm = ({ article = null, onSuccess = null, onCancel = null }) => {
  const [formData, setFormData] = useState({
    title: article?.title || '',
    excerpt: article?.excerpt || '',
    content: article?.content || '',
    image_url: article?.image_url || '',
    youtube_url: article?.youtube_url || '',
    category: article?.category || 'HBCU',
    is_premium: article?.is_premium || false,
    author: article?.author || 'SheLovesThee Team'
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(article?.image_url || '');
  const [submitting, setSubmitting] = useState(false);
  const [htmlImporting, setHtmlImporting] = useState(false);
  const [copiedContent, setCopiedContent] = useState(false);
  const contentTextareaRef = React.useRef(null);

  const categories = ['HBCU', 'SWAC', 'Jackson State', 'Football', 'Basketball', 'Recruiting'];

  // Handle inserting rich media embed code
  const handleRichMediaInsert = (embedCode) => {
    const textarea = contentTextareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentContent = formData.content;
      const newContent = currentContent.substring(0, start) + embedCode + currentContent.substring(end);
      setFormData({ ...formData, content: newContent });
      
      // Set cursor position after inserted content
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + embedCode.length;
        textarea.focus();
      }, 0);
    } else {
      // If no cursor position, append to end
      setFormData({ ...formData, content: formData.content + '\n' + embedCode });
    }
  };

  // Copy HTML content to clipboard
  const handleCopyContent = async () => {
    if (!formData.content) {
      toast.error('No content to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(formData.content);
      setCopiedContent(true);
      toast.success('HTML content copied to clipboard!');
      setTimeout(() => setCopiedContent(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy content');
    }
  };

  // Handle HTML file import
  const handleHtmlImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.html') && !file.name.endsWith('.htm')) {
      toast.error('Please upload an HTML file (.html or .htm)');
      return;
    }
    
    setHtmlImporting(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const htmlContent = event.target.result;
        
        // Parse HTML to extract content
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        
        // Try to extract title from <title> tag or <h1>
        const titleTag = doc.querySelector('title');
        const h1Tag = doc.querySelector('h1');
        const extractedTitle = titleTag?.textContent || h1Tag?.textContent || '';
        
        // Try to extract meta description for excerpt
        const metaDesc = doc.querySelector('meta[name="description"]');
        const extractedExcerpt = metaDesc?.getAttribute('content') || '';
        
        // Get the body content (the main HTML)
        const bodyContent = doc.body ? doc.body.innerHTML : htmlContent;
        
        // Update form with extracted content
        setFormData(prev => ({
          ...prev,
          title: extractedTitle || prev.title,
          excerpt: extractedExcerpt || prev.excerpt,
          content: bodyContent.trim()
        }));
        
        toast.success('HTML imported successfully! Review and edit as needed.');
      } catch (error) {
        console.error('Error parsing HTML:', error);
        toast.error('Failed to parse HTML file');
      } finally {
        setHtmlImporting(false);
        // Reset the file input
        e.target.value = '';
      }
    };
    reader.onerror = () => {
      toast.error('Failed to read HTML file');
      setHtmlImporting(false);
    };
    reader.readAsText(file);
  };

  // Update form when article prop changes
  React.useEffect(() => {
    if (article) {
      setFormData({
        title: article.title || '',
        excerpt: article.excerpt || '',
        content: article.content || '',
        image_url: article.image_url || '',
        youtube_url: article.youtube_url || '',
        category: article.category || 'HBCU',
        is_premium: article.is_premium || false,
        author: article.author || 'SheLovesThee Team'
      });
      setImagePreview(article.image_url || '');
    }
  }, [article]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImagePreview(base64String);
        setFormData({ ...formData, image_url: base64String });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (article) {
        // Update existing article
        await axios.put(`${API}/articles/${article.article_id}`, formData);
        toast.success('Article updated successfully!');
        if (onSuccess) onSuccess();
      } else {
        // Create new article
        await axios.post(`${API}/articles`, formData);
        toast.success('Article published successfully!');
        setFormData({
          title: '',
          excerpt: '',
          content: '',
          image_url: '',
          youtube_url: '',
          category: 'HBCU',
          is_premium: false,
          author: 'SheLovesThee Team'
        });
        setImageFile(null);
        setImagePreview('');
      }
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error(article ? 'Failed to update article' : 'Failed to publish article');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-lg space-y-6" data-testid="article-form">
      {article && onCancel && (
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold uppercase heading-font text-[#001f3f]">Edit Article</h2>
          <Button
            type="button"
            onClick={onCancel}
            variant="ghost"
            className="text-slate-600"
          >
            Cancel
          </Button>
        </div>
      )}
      <div>
        <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2" data-testid="label-title">
          Title *
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
          placeholder="Enter article title"
          required
          data-testid="input-title"
        />
      </div>

      <div>
        <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
          Excerpt *
        </label>
        <textarea
          value={formData.excerpt}
          onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
          className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
          rows="2"
          placeholder="Brief summary (appears in article cards)"
          required
          data-testid="input-excerpt"
        />
      </div>

      {/* HTML Import Tool */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Import HTML Article
            </h3>
            <p className="text-sm text-slate-600 mt-1">Upload a pre-coded HTML file to auto-fill the content below</p>
          </div>
          <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors">
            {htmlImporting ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span className="font-bold text-sm">Importing...</span>
              </>
            ) : (
              <>
                <PlusCircle className="h-4 w-4" />
                <span className="font-bold text-sm">Upload HTML</span>
              </>
            )}
            <input
              type="file"
              accept=".html,.htm"
              onChange={handleHtmlImport}
              className="hidden"
              disabled={htmlImporting}
              data-testid="input-html-import"
            />
          </label>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-bold uppercase tracking-wider text-slate-700">
            Content *
          </label>
          {formData.content && (
            <Button
              type="button"
              onClick={handleCopyContent}
              variant="ghost"
              size="sm"
              className={`flex items-center gap-1 text-sm ${copiedContent ? 'text-green-600' : 'text-blue-600 hover:text-blue-700'}`}
              data-testid="copy-content-btn"
            >
              {copiedContent ? (
                <>
                  <Check className="h-4 w-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy HTML
                </>
              )}
            </Button>
          )}
        </div>
        
        {/* Rich Media Builder Toolbar */}
        <RichMediaToolbar onInsert={handleRichMediaInsert} contentRef={contentTextareaRef} />
        
        <textarea
          ref={contentTextareaRef}
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent min-h-[300px] font-mono text-sm"
          placeholder="Write your article content here... (supports **bold**, *italic*, and basic formatting)"
          data-testid="input-content"
        />
        <p className="text-xs text-slate-500 mt-1">Tip: Use **text** for bold, *text* for italic. Use the toolbar above to embed videos, social posts, and charts.</p>
      </div>

      <div>
        <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
          Featured Image
        </label>
        <div className="space-y-3">
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2 px-4 py-2 bg-[#007AFF] text-white rounded-full cursor-pointer hover:bg-[#005bb5] transition-colors">
              <ImageIcon className="h-4 w-4" />
              <span className="font-bold text-sm">Upload Image</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                data-testid="input-image-upload"
              />
            </label>
            <span className="text-sm text-slate-600">or</span>
            <input
              type="url"
              value={imagePreview ? '' : formData.image_url}
              onChange={(e) => {
                setFormData({ ...formData, image_url: e.target.value });
                setImagePreview('');
                setImageFile(null);
              }}
              className="flex-1 p-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent text-sm"
              placeholder="Paste image URL"
              disabled={!!imagePreview}
              data-testid="input-image-url"
            />
          </div>
          {(imagePreview || formData.image_url) && (
            <div className="relative">
              <img 
                src={imagePreview || formData.image_url} 
                alt="Preview" 
                className="w-full h-48 object-cover rounded-lg border border-slate-200"
              />
              <button
                type="button"
                onClick={() => {
                  setImagePreview('');
                  setImageFile(null);
                  setFormData({ ...formData, image_url: '' });
                }}
                className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-2">Upload an image (max 5MB) or paste an image URL</p>
      </div>

      <div>
        <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
          YouTube URL
        </label>
        <input
          type="url"
          value={formData.youtube_url}
          onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
          className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
          placeholder="https://youtube.com/watch?v=..."
          data-testid="input-youtube-url"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
            Category *
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
            required
            data-testid="select-category"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
            Author
          </label>
          <input
            type="text"
            value={formData.author}
            onChange={(e) => setFormData({ ...formData, author: e.target.value })}
            className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
            placeholder="Author name"
            data-testid="input-author"
          />
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="is_premium"
          checked={formData.is_premium}
          onChange={(e) => setFormData({ ...formData, is_premium: e.target.checked })}
          className="h-5 w-5 text-[#007AFF] focus:ring-[#007AFF] border-slate-300 rounded"
          data-testid="checkbox-premium"
        />
        <label htmlFor="is_premium" className="text-sm font-medium text-slate-700">
          Members-only content
        </label>
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#007AFF] text-white px-8 py-3 rounded-full font-bold uppercase tracking-wider hover:scale-105 transition-transform"
        data-testid="submit-article-button"
      >
        {submitting ? (article ? 'Updating...' : 'Publishing...') : (article ? 'Update Article' : 'Publish Article')}
      </Button>
    </form>
  );
};

const PollForm = () => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '', '', '']);
  const [submitting, setSubmitting] = useState(false);
  const [polls, setPolls] = useState([]);
  const [articlePolls, setArticlePolls] = useState([]);
  const [loadingPolls, setLoadingPolls] = useState(true);

  // Fetch existing polls
  useEffect(() => {
    const fetchPolls = async () => {
      try {
        const [sitePolls, artPolls] = await Promise.all([
          axios.get(`${API}/polls`),
          axios.get(`${API}/article-polls/jsu-vs-aamu-2026`)
        ]);
        setPolls(sitePolls.data || []);
        setArticlePolls(artPolls.data || []);
      } catch (err) {
        console.error('Error fetching polls:', err);
      } finally {
        setLoadingPolls(false);
      }
    };
    fetchPolls();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const filteredOptions = options.filter(opt => opt.trim() !== '');
    
    if (filteredOptions.length < 2) {
      toast.error('Please provide at least 2 options');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/polls`, {
        question,
        options: filteredOptions
      });
      toast.success('Poll created successfully!');
      setQuestion('');
      setOptions(['', '', '', '']);
      // Refresh polls
      const res = await axios.get(`${API}/polls`);
      setPolls(res.data || []);
    } catch (error) {
      console.error('Error creating poll:', error);
      toast.error('Failed to create poll');
    } finally {
      setSubmitting(false);
    }
  };

  const getTotalVotes = (votes) => Object.values(votes || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-8">
      {/* Create Poll Form */}
      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-lg space-y-6" data-testid="poll-form">
        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <PlusCircle className="w-5 h-5" /> Create New Poll
        </h3>
        <div>
          <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
            Poll Question *
          </label>
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
            placeholder="What's your question?"
            required
            data-testid="input-poll-question"
          />
        </div>

        <div>
          <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
            Options (min 2) *
          </label>
          {options.map((option, index) => (
            <input
              key={index}
              type="text"
              value={option}
              onChange={(e) => {
                const newOptions = [...options];
                newOptions[index] = e.target.value;
                setOptions(newOptions);
              }}
              className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent mb-3"
              placeholder={`Option ${index + 1}`}
              data-testid={`input-poll-option-${index}`}
            />
          ))}
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-[#007AFF] text-white px-8 py-3 rounded-full font-bold uppercase tracking-wider hover:scale-105 transition-transform"
          data-testid="submit-poll-button"
        >
          {submitting ? 'Creating...' : 'Create Poll'}
        </Button>
      </form>

      {/* Poll Results Dashboard */}
      <div className="bg-white p-8 rounded-lg shadow-lg" data-testid="poll-results">
        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-6">
          <BarChart3 className="w-5 h-5" /> Poll Results Dashboard
        </h3>

        {loadingPolls ? (
          <div className="text-center py-8 text-slate-500">Loading polls...</div>
        ) : (
          <div className="space-y-6">
            {/* Site-wide Polls */}
            <div>
              <h4 className="text-sm font-bold uppercase text-slate-600 mb-4">Site-wide Polls ({polls.length})</h4>
              {polls.length === 0 ? (
                <p className="text-slate-500 text-sm">No site-wide polls yet.</p>
              ) : (
                <div className="space-y-4">
                  {polls.map((poll, idx) => (
                    <div key={poll.poll_id || idx} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <h5 className="font-semibold text-slate-800">{poll.question}</h5>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          {getTotalVotes(poll.votes)} votes
                        </span>
                      </div>
                      <div className="space-y-2">
                        {(poll.options || []).map((opt, i) => {
                          const votes = poll.votes?.[opt] || 0;
                          const total = getTotalVotes(poll.votes);
                          const pct = total > 0 ? Math.round((votes / total) * 100) : 0;
                          return (
                            <div key={i} className="relative">
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-slate-700">{opt}</span>
                                <span className="text-slate-500">{votes} ({pct}%)</span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-500 rounded-full transition-all"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Article Polls */}
            <div>
              <h4 className="text-sm font-bold uppercase text-slate-600 mb-4">
                Article Polls - JSU vs AAMU ({articlePolls.length})
              </h4>
              {articlePolls.length === 0 ? (
                <p className="text-slate-500 text-sm">No article polls yet.</p>
              ) : (
                <div className="space-y-4">
                  {articlePolls.map((poll, idx) => (
                    <div key={poll.poll_id || idx} className="border border-slate-200 rounded-lg p-4 bg-gradient-to-r from-blue-50 to-red-50">
                      <div className="flex justify-between items-start mb-3">
                        <h5 className="font-semibold text-slate-800">{poll.question}</h5>
                        <span className="text-xs bg-gradient-to-r from-blue-600 to-red-600 text-white px-2 py-1 rounded-full">
                          {poll.total_votes || getTotalVotes(poll.votes)} votes
                        </span>
                      </div>
                      <div className="space-y-2">
                        {(poll.options || []).map((opt, i) => {
                          const votes = poll.votes?.[opt] || 0;
                          const total = poll.total_votes || getTotalVotes(poll.votes);
                          const pct = total > 0 ? Math.round((votes / total) * 100) : 0;
                          return (
                            <div key={i} className="relative">
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-slate-700">{opt}</span>
                                <span className="text-slate-500">{votes} ({pct}%)</span>
                              </div>
                              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-blue-500 to-red-500 rounded-full transition-all"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PredictionForm = () => {
  const [formData, setFormData] = useState({
    game_title: '',
    team1: '',
    team2: '',
    team1_logo: '',
    team2_logo: '',
    game_date: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/predictions`, {
        ...formData,
        game_date: new Date(formData.game_date).toISOString()
      });
      toast.success('Prediction created successfully!');
      setFormData({
        game_title: '',
        team1: '',
        team2: '',
        team1_logo: '',
        team2_logo: '',
        game_date: ''
      });
    } catch (error) {
      console.error('Error creating prediction:', error);
      toast.error('Failed to create prediction');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-lg space-y-6" data-testid="prediction-form">
      <div>
        <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
          Game Title *
        </label>
        <input
          type="text"
          value={formData.game_title}
          onChange={(e) => setFormData({ ...formData, game_title: e.target.value })}
          className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
          placeholder="e.g., SWAC Championship 2024"
          required
          data-testid="input-game-title"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
            Team 1 *
          </label>
          <input
            type="text"
            value={formData.team1}
            onChange={(e) => setFormData({ ...formData, team1: e.target.value })}
            className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
            placeholder="Team name"
            required
            data-testid="input-team1"
          />
        </div>

        <div>
          <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
            Team 2 *
          </label>
          <input
            type="text"
            value={formData.team2}
            onChange={(e) => setFormData({ ...formData, team2: e.target.value })}
            className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
            placeholder="Team name"
            required
            data-testid="input-team2"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
            Team 1 Logo URL
          </label>
          <input
            type="url"
            value={formData.team1_logo}
            onChange={(e) => setFormData({ ...formData, team1_logo: e.target.value })}
            className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
            placeholder="https://example.com/logo1.png"
            data-testid="input-team1-logo"
          />
        </div>

        <div>
          <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
            Team 2 Logo URL
          </label>
          <input
            type="url"
            value={formData.team2_logo}
            onChange={(e) => setFormData({ ...formData, team2_logo: e.target.value })}
            className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
            placeholder="https://example.com/logo2.png"
            data-testid="input-team2-logo"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
          Game Date *
        </label>
        <input
          type="datetime-local"
          value={formData.game_date}
          onChange={(e) => setFormData({ ...formData, game_date: e.target.value })}
          className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
          required
          data-testid="input-game-date"
        />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#007AFF] text-white px-8 py-3 rounded-full font-bold uppercase tracking-wider hover:scale-105 transition-transform"
        data-testid="submit-prediction-button"
      >
        {submitting ? 'Creating...' : 'Create Prediction'}
      </Button>
    </form>
  );
};

// Score Predictions Results Dashboard
const ScorePredictionsResults = () => {
  const [predictions, setPredictions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPredictions = async () => {
      try {
        const res = await axios.get(`${API}/score-predictions/jsu-vs-aamu-2026`);
        setPredictions(res.data.predictions || []);
        setStats(res.data.stats || {});
      } catch (err) {
        console.error('Error fetching score predictions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPredictions();
  }, []);

  if (loading) {
    return <div className="text-center py-8 text-slate-500">Loading predictions...</div>;
  }

  return (
    <div className="bg-white p-8 rounded-lg shadow-lg mt-8" data-testid="score-predictions-results">
      <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2 mb-6">
        <Trophy className="w-5 h-5" /> Score Predictions - JSU vs AAMU
      </h3>

      {/* Stats Summary */}
      {stats.total_predictions > 0 && (
        <div className="bg-gradient-to-r from-blue-50 via-yellow-50 to-red-50 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-slate-800">{stats.total_predictions}</p>
              <p className="text-xs text-slate-500 uppercase">Total Picks</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{stats.avg_team1_score}</p>
              <p className="text-xs text-slate-500 uppercase">Avg JSU Score</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{stats.avg_team2_score}</p>
              <p className="text-xs text-slate-500 uppercase">Avg AAMU Score</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{stats.team1_win_pct}%</p>
              <p className="text-xs text-slate-500 uppercase">Pick JSU</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{stats.team2_win_pct}%</p>
              <p className="text-xs text-slate-500 uppercase">Pick AAMU</p>
            </div>
          </div>
        </div>
      )}

      {/* Predictions List */}
      {predictions.length === 0 ? (
        <p className="text-slate-500 text-center py-8">No score predictions yet.</p>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {predictions.map((pred, idx) => (
            <div 
              key={pred.prediction_id || idx} 
              className={`flex items-center justify-between p-3 rounded-lg border ${
                pred.team1_score > pred.team2_score 
                  ? 'border-blue-200 bg-blue-50' 
                  : pred.team2_score > pred.team1_score 
                    ? 'border-red-200 bg-red-50'
                    : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="font-semibold text-sm text-slate-700">{pred.user_name || 'Fan'}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className={`text-xl font-bold ${pred.team1_score > pred.team2_score ? 'text-blue-600' : 'text-slate-600'}`}>
                  {pred.team1_score}
                </span>
                <span className="text-slate-400">-</span>
                <span className={`text-xl font-bold ${pred.team2_score > pred.team1_score ? 'text-red-600' : 'text-slate-600'}`}>
                  {pred.team2_score}
                </span>
              </div>
              <span className="text-xs text-slate-400">
                {new Date(pred.created_at).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Wrap PredictionForm with results
const PredictionSection = () => (
  <div className="space-y-8">
    <PredictionForm />
    <ScorePredictionsResults />
  </div>
);

const QBClubManager = () => {
  const [config, setConfig] = useState(null);
  const [roster, setRoster] = useState([]);
  const [seasonTotals, setSeasonTotals] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [syncLog, setSyncLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeSection, setActiveSection] = useState('roster');
  const [editingQB, setEditingQB] = useState(null);
  const [newPhotoUrl, setNewPhotoUrl] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [configRes, rosterRes, totalsRes] = await Promise.all([
        axios.get(`${API}/qb/config`),
        axios.get(`${API}/qb/roster`),
        axios.get(`${API}/qb/season-totals`)
      ]);
      setConfig(configRes.data);
      setRoster(rosterRes.data);
      setSeasonTotals(totalsRes.data);
      
      // Fetch pending approvals (requires password)
      try {
        const adminPassword = sessionStorage.getItem('adminPassword') || 'Braxton2017!';
        const pendingRes = await axios.get(`${API}/qb/pending-approval?password=${encodeURIComponent(adminPassword)}`);
        setPendingApprovals(pendingRes.data);
      } catch (e) {
        console.log('Could not fetch pending approvals');
      }
    } catch (error) {
      console.error('Error fetching QB data:', error);
      toast.error('Failed to load QB data');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncPreview = async () => {
    setSyncing(true);
    try {
      const adminPassword = 'Braxton2017!';
      const res = await axios.post(`${API}/qb/swac-sync/preview?password=${encodeURIComponent(adminPassword)}`);
      toast.info(`Sync Preview: ${res.data.previews?.length || 0} players found`);
      console.log('Sync Preview:', res.data);
    } catch (error) {
      toast.error('Failed to preview sync');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncExecute = async () => {
    if (!window.confirm('Are you sure you want to sync QB stats from SWAC? This will update season totals.')) return;
    
    setSyncing(true);
    try {
      const adminPassword = 'Braxton2017!';
      const res = await axios.post(`${API}/qb/swac-sync/execute?password=${encodeURIComponent(adminPassword)}`);
      toast.success(`Sync complete: ${res.data.saved_count || 0} players updated`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to sync');
    } finally {
      setSyncing(false);
    }
  };

  const handleSetWeek = async (week) => {
    try {
      const adminPassword = 'Braxton2017!';
      await axios.post(`${API}/qb/config/set-week?week=${week}&password=${encodeURIComponent(adminPassword)}`);
      toast.success(`Current week set to ${week}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to set week');
    }
  };

  const handleUpdatePhoto = async (playerId) => {
    if (!newPhotoUrl) {
      toast.error('Please enter a photo URL');
      return;
    }
    try {
      const adminPassword = 'Braxton2017!';
      await axios.post(`${API}/qb/roster/${playerId}/update-photo?photo_url=${encodeURIComponent(newPhotoUrl)}&password=${encodeURIComponent(adminPassword)}`);
      toast.success('Photo updated successfully');
      setEditingQB(null);
      setNewPhotoUrl('');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update photo');
    }
  };

  const handleUpdateStatus = async (playerId, status, note = null) => {
    try {
      const adminPassword = 'Braxton2017!';
      let url = `${API}/qb/roster/${playerId}/update-status?password=${encodeURIComponent(adminPassword)}`;
      
      if (status === 'active') {
        url += '&active_in_rankings=true&injury_status=';
      } else if (status === 'OUT_TEMPORARILY') {
        url += '&active_in_rankings=false&injury_status=OUT_TEMPORARILY';
        if (note) url += `&injury_note=${encodeURIComponent(note)}`;
      } else if (status === 'OUT_FOR_SEASON') {
        url += '&active_in_rankings=false&injury_status=OUT_FOR_SEASON';
      }
      
      await axios.post(url);
      toast.success('Status updated successfully');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleApproveQB = async (name, school) => {
    try {
      const adminPassword = 'Braxton2017!';
      const res = await axios.post(`${API}/qb/approve-qb?name=${encodeURIComponent(name)}&school=${encodeURIComponent(school)}&password=${encodeURIComponent(adminPassword)}`);
      toast.success(`${res.data.message}${res.data.stats_saved ? ' with SWAC stats' : ''}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to approve QB');
    }
  };

  const handleRejectQB = async (name, school) => {
    try {
      const adminPassword = 'Braxton2017!';
      await axios.post(`${API}/qb/reject-qb?name=${encodeURIComponent(name)}&school=${encodeURIComponent(school)}&password=${encodeURIComponent(adminPassword)}`);
      toast.success(`Rejected ${name}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to reject QB');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading QB system...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header with Config Info */}
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold uppercase heading-font text-[#001f3f]">2026 QB System Manager</h2>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">Current Week: <strong>{config?.current_week}</strong></span>
            <span className="text-sm text-slate-600">Last Sync: <strong>{config?.last_sync ? new Date(config.last_sync).toLocaleDateString() : 'Never'}</strong></span>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3">
          <Button onClick={handleSyncPreview} disabled={syncing} className="bg-blue-500 hover:bg-blue-600 text-white">
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Preview SWAC Sync
          </Button>
          <Button onClick={handleSyncExecute} disabled={syncing} className="bg-green-500 hover:bg-green-600 text-white">
            <Database className="h-4 w-4 mr-2" />
            Execute SWAC Sync
          </Button>
          <select 
            className="border rounded px-3 py-2"
            value={config?.current_week || 6}
            onChange={(e) => handleSetWeek(parseInt(e.target.value))}
          >
            {[1,2,3,4,5,6,7,8,9,10,11,12].map(w => (
              <option key={w} value={w}>Set Week {w}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        {['roster', 'stats', 'pending', 'awards', 'linking'].map(section => (
          <button
            key={section}
            data-testid={`qb-section-${section}`}
            onClick={() => setActiveSection(section)}
            className={`px-4 py-2 font-medium rounded-t ${activeSection === section ? 'bg-[#001f3f] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            {section === 'roster' && 'QB Roster'}
            {section === 'stats' && 'Season Stats'}
            {section === 'pending' && `Pending Approval (${pendingApprovals.length})`}
            {section === 'awards' && 'Weekly Awards'}
            {section === 'linking' && "Link Pick'em Results"}
          </button>
        ))}
      </div>

      {/* Roster Section */}
      {activeSection === 'roster' && (
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-xl font-bold text-[#001f3f] mb-4">2026 QB Roster ({roster.length} QBs)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roster.map(qb => (
              <div key={qb.player_id} className={`border rounded-lg overflow-hidden ${qb.injury_status ? 'border-yellow-400 bg-yellow-50' : 'border-slate-200'}`}>
                <div className="h-32 bg-slate-100">
                  {qb.photo_url ? (
                    <img src={qb.photo_url} alt={qb.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">No Photo</div>
                  )}
                </div>
                <div className="p-3">
                  <h4 className="font-bold text-[#001f3f]">{qb.name}</h4>
                  <p className="text-sm text-slate-600">{qb.school}</p>
                  {qb.injury_status && (
                    <span className="inline-block bg-yellow-500 text-white text-xs px-2 py-1 rounded mt-1">
                      {qb.injury_status === 'OUT_TEMPORARILY' ? 'OUT TEMP' : 'OUT SEASON'}
                    </span>
                  )}
                  
                  {/* Edit Controls */}
                  {editingQB === qb.player_id ? (
                    <div className="mt-2 space-y-2">
                      <input
                        type="text"
                        placeholder="New photo URL"
                        value={newPhotoUrl}
                        onChange={(e) => setNewPhotoUrl(e.target.value)}
                        className="w-full text-sm border rounded px-2 py-1"
                      />
                      <div className="flex gap-1">
                        <Button size="sm" onClick={() => handleUpdatePhoto(qb.player_id)} className="flex-1 text-xs">Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditingQB(null); setNewPhotoUrl(''); }} className="flex-1 text-xs">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditingQB(qb.player_id)} className="text-xs">
                        <ImageIcon className="h-3 w-3 mr-1" /> Photo
                      </Button>
                      {qb.injury_status ? (
                        <Button size="sm" variant="ghost" onClick={() => handleUpdateStatus(qb.player_id, 'active')} className="text-xs text-green-600">
                          Mark Active
                        </Button>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => handleUpdateStatus(qb.player_id, 'OUT_TEMPORARILY', 'RETURN TBD')} className="text-xs text-yellow-600">
                            Out Temp
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleUpdateStatus(qb.player_id, 'OUT_FOR_SEASON')} className="text-xs text-red-600">
                            Out Season
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Section */}
      {activeSection === 'stats' && (
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-xl font-bold text-[#001f3f] mb-4">Current Season Totals (SWAC Official)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-2 py-2 text-left">Player</th>
                  <th className="px-2 py-2">G</th>
                  <th className="px-2 py-2">COMP</th>
                  <th className="px-2 py-2">ATT</th>
                  <th className="px-2 py-2">YDS</th>
                  <th className="px-2 py-2">TD</th>
                  <th className="px-2 py-2">INT</th>
                  <th className="px-2 py-2">EFFIC</th>
                </tr>
              </thead>
              <tbody>
                {seasonTotals.map((qb, i) => (
                  <tr key={qb.player_id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="px-2 py-2 font-medium">{qb.name}</td>
                    <td className="px-2 py-2 text-center">{qb.games_played || 0}</td>
                    <td className="px-2 py-2 text-center">{qb.completions || 0}</td>
                    <td className="px-2 py-2 text-center">{qb.attempts || 0}</td>
                    <td className="px-2 py-2 text-center font-bold text-blue-600">{qb.passing_yards || 0}</td>
                    <td className="px-2 py-2 text-center font-bold text-green-600">{qb.passing_tds || 0}</td>
                    <td className="px-2 py-2 text-center text-red-500">{qb.interceptions || 0}</td>
                    <td className="px-2 py-2 text-center">{qb.passing_efficiency?.toFixed(1) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pending Approvals Section */}
      {activeSection === 'pending' && (
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-xl font-bold text-[#001f3f] mb-4">New QBs Pending Approval</h3>
          {pendingApprovals.length === 0 ? (
            <p className="text-slate-500">No pending QB approvals.</p>
          ) : (
            <div className="space-y-4">
              {pendingApprovals.map((qb, i) => (
                <div key={i} className="border border-orange-200 bg-orange-50 rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-[#001f3f]">{qb.name?.includes(',') ? qb.name.split(',').reverse().map(x => x.trim()).join(' ') : qb.name}</h4>
                    <p className="text-sm text-slate-600">{qb.school}</p>
                    <p className="text-xs text-slate-500">Detected: {qb.detected_at ? new Date(qb.detected_at).toLocaleDateString() : 'Unknown'}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleApproveQB(qb.name, qb.school)} className="bg-green-500 hover:bg-green-600 text-white" data-testid={`approve-qb-${i}`}>
                      Approve
                    </Button>
                    <Button onClick={() => handleRejectQB(qb.name, qb.school)} variant="ghost" className="text-red-600">
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeSection === 'awards' && <QBAwardsAdmin roster={roster} currentWeek={config?.current_week} />}

      {activeSection === 'linking' && <QBPickemLinker currentWeek={config?.current_week} />}
    </div>
  );
};

const QBForm = ({ qb = null, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: qb?.name || '',
    team: qb?.team || '',
    bio: qb?.bio || '',
    image_url: qb?.image_url || '',
    featured: qb?.featured || false
  });
  const [stats, setStats] = useState(qb?.stats || {
    'Passing Yards': '',
    'Passing TDs': '',
    'Rushing TDs': '',
    'Completion %': '',
    'RTG': ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(qb?.image_url || '');
  const [submitting, setSubmitting] = useState(false);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImagePreview(base64String);
        setFormData({ ...formData, image_url: base64String });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleStatChange = (key, value) => {
    setStats({ ...stats, [key]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (qb) {
        // Update existing QB
        await axios.put(`${API}/qb-club/${qb.qb_id}`, {
          ...formData,
          stats
        });
        toast.success('QB profile updated successfully!');
      } else {
        // Create new QB
        await axios.post(`${API}/qb-club`, {
          ...formData,
          stats
        });
        toast.success('QB profile created successfully!');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving QB profile:', error);
      toast.error(qb ? 'Failed to update QB profile' : 'Failed to create QB profile');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-lg space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold uppercase heading-font text-[#001f3f]">
          {qb ? 'Edit QB Profile' : 'Add QB Profile'}
        </h2>
        <Button
          type="button"
          onClick={onCancel}
          variant="ghost"
          className="text-slate-600"
        >
          Cancel
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
            QB Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
            placeholder="e.g., Shedeur Sanders"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
            Team *
          </label>
          <input
            type="text"
            value={formData.team}
            onChange={(e) => setFormData({ ...formData, team: e.target.value })}
            className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
            placeholder="e.g., Jackson State Tigers"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
          Bio *
        </label>
        <textarea
          value={formData.bio}
          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
          className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
          rows="3"
          placeholder="Brief biography and playing style"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
          QB Photo
        </label>
        <div className="space-y-3">
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2 px-4 py-2 bg-[#007AFF] text-white rounded-full cursor-pointer hover:bg-[#005bb5] transition-colors">
              <ImageIcon className="h-4 w-4" />
              <span className="font-bold text-sm">Upload Photo</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
            <span className="text-sm text-slate-600">or</span>
            <input
              type="url"
              value={imagePreview ? '' : formData.image_url}
              onChange={(e) => {
                setFormData({ ...formData, image_url: e.target.value });
                setImagePreview('');
                setImageFile(null);
              }}
              className="flex-1 p-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent text-sm"
              placeholder="Paste image URL"
              disabled={!!imagePreview}
            />
          </div>
          {(imagePreview || formData.image_url) && (
            <div className="relative">
              <img 
                src={imagePreview || formData.image_url} 
                alt="Preview" 
                className="w-full h-64 object-cover rounded-lg border border-slate-200"
              />
              <button
                type="button"
                onClick={() => {
                  setImagePreview('');
                  setImageFile(null);
                  setFormData({ ...formData, image_url: '' });
                }}
                className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
          Stats
        </label>
        <div className="grid grid-cols-2 gap-4">
          {Object.keys(stats).map(statKey => (
            <div key={statKey}>
              <label className="block text-xs text-slate-600 mb-1">{statKey}</label>
              <input
                type="text"
                value={stats[statKey]}
                onChange={(e) => handleStatChange(statKey, e.target.value)}
                className="w-full p-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent text-sm"
                placeholder={
                  statKey === 'Passing Yards' ? '3,231' :
                  statKey === 'Passing TDs' ? '32' :
                  statKey === 'Rushing TDs' ? '8' :
                  statKey === 'Completion %' ? '67.4' :
                  statKey === 'RTG' ? '156.8' : ''
                }
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-2">RTG = Passer Rating</p>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="featured"
          checked={formData.featured}
          onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
          className="h-5 w-5 text-[#007AFF] focus:ring-[#007AFF] border-slate-300 rounded"
        />
        <label htmlFor="featured" className="text-sm font-medium text-slate-700">
          Featured QB (shows gold badge)
        </label>
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#007AFF] text-white px-8 py-3 rounded-full font-bold uppercase tracking-wider hover:scale-105 transition-transform"
      >
        {submitting ? (qb ? 'Updating...' : 'Creating...') : (qb ? 'Update QB Profile' : 'Create QB Profile')}
      </Button>
    </form>
  );
};

const ScheduleManager = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGame, setEditingGame] = useState(null);

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      const response = await axios.get(`${API}/schedule`);
      setGames(response.data);
    } catch (error) {
      console.error('Error fetching schedule:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (gameId) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;

    try {
      await axios.delete(`${API}/schedule/${gameId}`);
      toast.success('Game deleted');
      fetchGames();
    } catch (error) {
      console.error('Error deleting game:', error);
      toast.error('Failed to delete game');
    }
  };

  const handleEdit = (game) => {
    setEditingGame(game);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingGame(null);
    fetchGames();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingGame(null);
  };

  if (loading) {
    return <div className="text-center py-12">Loading schedule...</div>;
  }

  return (
    <div className="space-y-6">
      {!showForm ? (
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold uppercase heading-font text-[#001f3f]">Manage Schedule</h2>
            <Button
              onClick={() => { setEditingGame(null); setShowForm(true); }}
              className="bg-[#007AFF] text-white px-6 py-2 rounded-full font-bold uppercase"
              data-testid="add-game-btn"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Game
            </Button>
          </div>
          
          {games.length === 0 ? (
            <p className="text-center text-slate-600 py-8">No games scheduled yet. Add your first one!</p>
          ) : (
            <div className="space-y-3">
              {games.map(game => (
                <div key={game.game_id} className="border border-slate-200 p-4 rounded-lg flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex items-center space-x-4">
                    {game.opponent_logo ? (
                      <img 
                        src={game.opponent_logo} 
                        alt={game.opponent}
                        className="h-12 w-12 object-contain rounded"
                      />
                    ) : (
                      <div className="h-12 w-12 bg-slate-200 rounded flex items-center justify-center">
                        <ImageIcon className="h-6 w-6 text-slate-400" />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-slate-700">{game.date}</span>
                        {!game.is_home && <span className="text-sm text-slate-500">at</span>}
                        <span className="font-semibold text-[#001f3f]">{game.opponent}</span>
                        {game.is_swac && <span className="text-[#DC2626] font-bold">*</span>}
                      </div>
                      <p className="text-sm text-slate-500">{game.location} • {game.kickoff_time}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => handleEdit(game)}
                      variant="ghost"
                      size="sm"
                      className="text-[#007AFF] hover:text-[#005bb5] hover:bg-blue-50"
                      data-testid={`edit-game-${game.game_id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(game.game_id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-game-${game.game_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-sm text-slate-500 mt-4">* SWAC Conference game</p>
        </div>
      ) : (
        <ScheduleForm game={editingGame} onSuccess={handleFormSuccess} onCancel={handleFormCancel} />
      )}
    </div>
  );
};

const ScheduleForm = ({ game = null, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    date: game?.date || '',
    opponent: game?.opponent || '',
    opponent_logo: game?.opponent_logo || '',
    location: game?.location || '',
    is_home: game?.is_home ?? true,
    is_swac: game?.is_swac ?? false,
    kickoff_time: game?.kickoff_time || 'TBA'
  });
  const [imagePreview, setImagePreview] = useState(game?.opponent_logo || '');
  const [submitting, setSubmitting] = useState(false);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo size should be less than 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImagePreview(base64String);
        setFormData({ ...formData, opponent_logo: base64String });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (game) {
        // Update existing game
        await axios.put(`${API}/schedule/${game.game_id}`, formData);
        toast.success('Game updated successfully!');
      } else {
        // Create new game
        await axios.post(`${API}/schedule`, formData);
        toast.success('Game added successfully!');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving game:', error);
      toast.error(game ? 'Failed to update game' : 'Failed to add game');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg shadow-lg space-y-6" data-testid="schedule-form">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold uppercase heading-font text-[#001f3f]">
          {game ? 'Edit Game' : 'Add Game'}
        </h2>
        <Button
          type="button"
          onClick={onCancel}
          variant="ghost"
          className="text-slate-600"
        >
          Cancel
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
            Date *
          </label>
          <input
            type="text"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
            placeholder="e.g., 9/5 or Sept 5"
            required
            data-testid="input-game-date"
          />
        </div>

        <div>
          <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
            Kickoff Time
          </label>
          <input
            type="text"
            value={formData.kickoff_time}
            onChange={(e) => setFormData({ ...formData, kickoff_time: e.target.value })}
            className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
            placeholder="e.g., 7:00 PM or TBA"
            data-testid="input-kickoff-time"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
          Opponent *
        </label>
        <input
          type="text"
          value={formData.opponent}
          onChange={(e) => setFormData({ ...formData, opponent: e.target.value })}
          className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
          placeholder="e.g., Southern University or OFF (for bye week)"
          required
          data-testid="input-opponent"
        />
      </div>

      <div>
        <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
          Opponent Logo
        </label>
        <div className="space-y-3">
          <div className="flex items-center space-x-4">
            <label className="flex items-center space-x-2 px-4 py-2 bg-[#007AFF] text-white rounded-full cursor-pointer hover:bg-[#005bb5] transition-colors">
              <ImageIcon className="h-4 w-4" />
              <span className="font-bold text-sm">Upload Logo</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                data-testid="input-logo-upload"
              />
            </label>
            <span className="text-sm text-slate-600">or</span>
            <input
              type="url"
              value={imagePreview ? '' : formData.opponent_logo}
              onChange={(e) => {
                setFormData({ ...formData, opponent_logo: e.target.value });
                setImagePreview('');
              }}
              className="flex-1 p-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent text-sm"
              placeholder="Paste logo URL"
              disabled={!!imagePreview}
              data-testid="input-logo-url"
            />
          </div>
          {(imagePreview || formData.opponent_logo) && (
            <div className="relative inline-block">
              <img 
                src={imagePreview || formData.opponent_logo} 
                alt="Logo Preview" 
                className="h-24 w-24 object-contain rounded-lg border border-slate-200 bg-white p-2"
              />
              <button
                type="button"
                onClick={() => {
                  setImagePreview('');
                  setFormData({ ...formData, opponent_logo: '' });
                }}
                className="absolute -top-2 -right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-2">Upload a logo (max 2MB) or paste a URL. This will display on the schedule.</p>
      </div>

      <div>
        <label className="block text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">
          Location *
        </label>
        <input
          type="text"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          className="w-full p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#007AFF] focus:border-transparent"
          placeholder="e.g., Jackson, MS or Baton Rouge, LA"
          required
          data-testid="input-location"
        />
      </div>

      <div className="flex items-center space-x-6">
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="is_home"
            checked={formData.is_home}
            onChange={(e) => setFormData({ ...formData, is_home: e.target.checked })}
            className="h-5 w-5 text-[#007AFF] focus:ring-[#007AFF] border-slate-300 rounded"
            data-testid="checkbox-is-home"
          />
          <label htmlFor="is_home" className="text-sm font-medium text-slate-700">
            Home Game
          </label>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="is_swac"
            checked={formData.is_swac}
            onChange={(e) => setFormData({ ...formData, is_swac: e.target.checked })}
            className="h-5 w-5 text-[#DC2626] focus:ring-[#DC2626] border-slate-300 rounded"
            data-testid="checkbox-is-swac"
          />
          <label htmlFor="is_swac" className="text-sm font-medium text-slate-700">
            SWAC Conference Game
          </label>
        </div>
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#007AFF] text-white px-8 py-3 rounded-full font-bold uppercase tracking-wider hover:scale-105 transition-transform"
        data-testid="submit-schedule-btn"
      >
        {submitting ? (game ? 'Updating...' : 'Adding...') : (game ? 'Update Game' : 'Add Game')}
      </Button>
    </form>
  );
};

// Admin Tools Component with Seed Database button
const AdminTools = () => {
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const handleSeedDatabase = async () => {
    if (!window.confirm('This will refresh all editorial content (articles, polls, predictions, QB club, schedule). User data (accounts, comments, picks) will be preserved. Continue?')) {
      return;
    }

    setSeeding(true);
    setSeedResult(null);
    
    try {
      const response = await axios.get(`${API}/admin/seed-now?password=Braxton0604!!!`);
      setSeedResult({ success: true, message: response.data.message });
      toast.success('Database seeded successfully!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to seed database';
      setSeedResult({ success: false, message: errorMsg });
      toast.error(errorMsg);
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8" data-testid="admin-tools">
      <h2 className="text-2xl font-bold uppercase text-[#001f3f] mb-6 heading-font">Admin Tools</h2>
      
      <div className="space-y-6">
        {/* Seed Database Section */}
        <div className="border border-slate-200 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="bg-[#001f3f] p-3 rounded-lg">
              <Database className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-[#001f3f] mb-2">Seed Database</h3>
              <p className="text-slate-600 text-sm mb-4">
                Refresh all editorial content including articles, polls, predictions, QB club data, and game schedules. 
                User accounts, comments, and picks will be preserved.
              </p>
              
              <Button
                onClick={handleSeedDatabase}
                disabled={seeding}
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold uppercase px-6 py-2"
                data-testid="seed-database-btn"
              >
                {seeding ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  <>
                    <Database className="h-4 w-4 mr-2" />
                    Seed Database
                  </>
                )}
              </Button>

              {seedResult && (
                <div className={`mt-4 p-3 rounded-lg ${seedResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {seedResult.message}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Interactive Article Info */}
        <div className="border border-slate-200 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="bg-[#007AFF] p-3 rounded-lg">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-[#001f3f] mb-2">Interactive Articles</h3>
              <p className="text-slate-600 text-sm mb-4">
                Interactive articles like the BoomBox Classic and Soccer Preview are custom-built components with player spotlights, 
                slideshows, and dynamic content. To create new interactive articles, contact your developer.
              </p>
              <div className="bg-slate-100 p-4 rounded-lg">
                <p className="text-sm font-bold text-slate-700 mb-2">Current Interactive Articles:</p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• <a href="/boombox-classic" className="text-[#007AFF] hover:underline">BoomBox Classic 2026</a></li>
                  <li>• <a href="/jackson-state-soccer" className="text-[#007AFF] hover:underline">Jackson State Soccer Preview</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Scoreboard Settings Manager - Control visibility schedule
// ============================================
// Game of the Week Video Manager
const GOTWVideoManager = () => {
  const [settings, setSettings] = useState({
    video_url: '',
    video_title: 'SheLovesThee Game of the Week Live',
    caption: 'JSU vs Grambling Coverage',
    link_url: '/swac-pickem',
    link_text: 'Make Your Picks'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/gotw-video`);
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching GOTW settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const pwd = sessionStorage.getItem('adminPassword') || 'Braxton0604!!!';
      await axios.post(`${API}/admin/settings/gotw-video?password=${encodeURIComponent(pwd)}`, settings);
      toast.success('Game of the Week video updated!');
    } catch (error) {
      toast.error('Failed to save settings');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  // Extract video ID for preview
  const getVideoId = (url) => {
    if (!url) return null;
    if (url.includes('youtube.com/embed/')) {
      return url.split('embed/')[1]?.split('?')[0];
    }
    if (url.includes('youtube.com/watch?v=')) {
      return url.split('v=')[1]?.split('&')[0];
    }
    if (url.includes('youtu.be/')) {
      return url.split('youtu.be/')[1]?.split('?')[0];
    }
    return null;
  };

  const videoId = getVideoId(settings.video_url);
  const previewUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : null;

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="animate-pulse flex items-center gap-2">
          <div className="h-6 w-6 bg-gray-200 rounded"></div>
          <div className="h-6 w-48 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6" data-testid="gotw-video-manager">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-red-100 p-2 rounded-lg">
            <Play className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#00204E]">Game of the Week Video</h2>
            <p className="text-sm text-slate-600">Update the YouTube video on the homepage sidebar</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700">
          {saving ? 'Saving...' : 'Update Video'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">YouTube Video URL</label>
            <Input
              value={settings.video_url}
              onChange={(e) => setSettings({...settings, video_url: e.target.value})}
              placeholder="https://www.youtube.com/watch?v=... or embed URL"
              className="w-full"
              data-testid="gotw-video-url"
            />
            <p className="text-xs text-gray-500 mt-1">Accepts: youtube.com/watch?v=, youtu.be/, or embed URLs</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Video Title</label>
            <Input
              value={settings.video_title}
              onChange={(e) => setSettings({...settings, video_title: e.target.value})}
              placeholder="SheLovesThee Game of the Week Live"
              className="w-full"
              data-testid="gotw-video-title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Caption (below video)</label>
            <Input
              value={settings.caption}
              onChange={(e) => setSettings({...settings, caption: e.target.value})}
              placeholder="JSU vs Grambling Coverage"
              className="w-full"
              data-testid="gotw-video-caption"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link URL</label>
              <Input
                value={settings.link_url}
                onChange={(e) => setSettings({...settings, link_url: e.target.value})}
                placeholder="/swac-pickem"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link Text</label>
              <Input
                value={settings.link_text}
                onChange={(e) => setSettings({...settings, link_text: e.target.value})}
                placeholder="Make Your Picks"
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Preview */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gradient-to-r from-[#0b4e9b] to-[#8e0e2c] px-3 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Play className="w-4 h-4 text-white animate-pulse" />
                <span className="text-white text-xs font-bold uppercase tracking-wide">Live Now: Game of the Week</span>
              </div>
              <span className="text-red-300 text-[10px] font-semibold animate-pulse">● LIVE</span>
            </div>
            <div className="aspect-video bg-gray-100">
              {previewUrl ? (
                <iframe 
                  width="100%" 
                  height="100%" 
                  src={`${previewUrl}?autoplay=0&rel=0`}
                  title={settings.video_title}
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <Play className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Enter a YouTube URL to preview</p>
                  </div>
                </div>
              )}
            </div>
            <div className="px-3 py-2 bg-gray-50 flex items-center justify-between">
              <span className="text-xs text-gray-600">{settings.caption || 'Caption'}</span>
              <span className="text-xs text-red-600 font-semibold flex items-center gap-1">
                {settings.link_text || 'Link Text'} <ChevronRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
        <strong>Tip:</strong> Changes appear on the homepage immediately after saving. Refresh the homepage to see updates.
      </div>
    </div>
  );
};

const ElectionModeManager = () => {
  const [mode, setMode] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try { setMode((await axios.get(`${API}/election/dashboard`)).data.mode); } catch { toast.error('Election mode status could not load'); }
  };
  useEffect(() => { load(); }, []);
  const toggle = async () => {
    setSaving(true);
    try {
      const password = sessionStorage.getItem('adminPassword');
      const response = await axios.post(`${API}/election/admin/mode`, null, { params: { active: !mode?.is_active }, headers: { 'X-Admin-Password': password } });
      setMode(response.data);
      toast.success(response.data.is_active ? 'Election Night Mode is active' : 'Sports homepage restored');
    } catch (error) { toast.error(error.response?.data?.detail || 'Election mode could not be changed'); }
    finally { setSaving(false); }
  };
  return <section className="mb-6 border-l-4 border-red-700 bg-white p-4 shadow-sm" data-testid="election-mode-admin-card"><div className="flex flex-wrap items-center justify-between gap-3"><div><p className="text-xs font-bold uppercase tracking-widest text-red-700">Temporary homepage takeover</p><h2 className="text-xl font-bold text-[#00204E]">Election Night Mode</h2><p className="text-sm text-slate-600">{mode?.is_active ? `Active. Scheduled sports restoration: ${mode.auto_revert_at}` : 'Sports-first homepage is active.'}</p></div><Button onClick={toggle} disabled={saving || !mode} className={mode?.is_active ? 'bg-red-700 text-white' : 'bg-[#00204E] text-white'} data-testid="admin-election-toggle-switch">{saving ? 'Saving…' : mode?.is_active ? 'Restore Sports Homepage' : 'Activate Election Night Mode'}</Button></div></section>;
};

const ScoreboardSettingsManager = () => {
  const [displayMode, setDisplayMode] = useState('automatic');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/scoreboard/status`);
      setStatus(response.data);
      setDisplayMode(response.data.display_mode || 'automatic');
    } catch (error) {
      console.error('Error fetching scoreboard status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/scoreboard`, { display_mode: displayMode });
      toast.success('Scoreboard settings saved!');
      fetchStatus(); // Refresh status after save
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="animate-pulse flex items-center gap-2">
          <div className="h-6 w-6 bg-gray-200 rounded"></div>
          <div className="h-6 w-48 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6" data-testid="scoreboard-settings-manager">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-blue-100 p-2 rounded-lg">
            <BarChart3 className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#00204E]">SWAC Scoreboard Settings</h2>
            <p className="text-sm text-slate-600">Control homepage scoreboard visibility</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#00204E]">
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Current Status Card */}
      {status && (
        <div className={`border rounded-lg p-4 mb-6 ${status.current_visibility ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-3 h-3 rounded-full ${status.current_visibility ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
            <span className="font-bold text-lg">
              {status.current_visibility ? 'Scoreboard VISIBLE' : 'Scoreboard HIDDEN'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Current Time (ET):</span>
              <p className="font-medium">{status.current_time_et}</p>
            </div>
            <div>
              <span className="text-gray-500">Next Scheduled Change:</span>
              <p className="font-medium">{status.next_scheduled_change}</p>
            </div>
          </div>
        </div>
      )}

      {/* Display Mode Selection */}
      <div className="space-y-4">
        <h3 className="font-bold text-slate-700">Display Mode</h3>
        
        <label className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${displayMode === 'automatic' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
          <input
            type="radio"
            name="display_mode"
            value="automatic"
            checked={displayMode === 'automatic'}
            onChange={(e) => setDisplayMode(e.target.value)}
            className="mt-1"
            data-testid="scoreboard-mode-automatic"
          />
          <div>
            <div className="font-semibold text-slate-800">🔄 Automatic (Recommended)</div>
            <p className="text-sm text-slate-600 mt-1">
              Follow the weekly schedule: <strong>Friday 6:00 AM ET → Monday 6:00 AM ET</strong> = Visible.
              Automatically hides Monday through Thursday.
            </p>
          </div>
        </label>

        <label className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${displayMode === 'always_show' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'}`}>
          <input
            type="radio"
            name="display_mode"
            value="always_show"
            checked={displayMode === 'always_show'}
            onChange={(e) => setDisplayMode(e.target.value)}
            className="mt-1"
            data-testid="scoreboard-mode-always-show"
          />
          <div>
            <div className="font-semibold text-slate-800">✅ Always Show</div>
            <p className="text-sm text-slate-600 mt-1">
              Override the automatic schedule. Scoreboard will be visible 24/7 until you change this setting.
            </p>
          </div>
        </label>

        <label className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${displayMode === 'always_hide' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`}>
          <input
            type="radio"
            name="display_mode"
            value="always_hide"
            checked={displayMode === 'always_hide'}
            onChange={(e) => setDisplayMode(e.target.value)}
            className="mt-1"
            data-testid="scoreboard-mode-always-hide"
          />
          <div>
            <div className="font-semibold text-slate-800">❌ Always Hide</div>
            <p className="text-sm text-slate-600 mt-1">
              Override the automatic schedule. Scoreboard will remain hidden until you change this setting.
            </p>
          </div>
        </label>
      </div>

      {/* Schedule Reference */}
      <div className="mt-6 p-4 bg-slate-50 rounded-lg">
        <h4 className="font-bold text-slate-700 mb-2">📅 Automatic Schedule Reference</h4>
        <div className="text-sm text-slate-600 space-y-1">
          <p>• <strong>Friday 6:00 AM ET</strong> → Scoreboard appears on homepage</p>
          <p>• <strong>Saturday & Sunday</strong> → Scoreboard visible (game days)</p>
          <p>• <strong>Monday 6:00 AM ET</strong> → Scoreboard hides automatically</p>
          <p>• <strong>Monday - Thursday</strong> → Scoreboard hidden, space collapses</p>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Note: Data continues updating even when hidden. Scoreboard is ready when visibility returns.
        </p>
      </div>
    </div>
  );
};



// Homepage Layout Manager - Control Featured/Secondary stories and carousel order
const HomepageLayoutManager = () => {
  const [articles, setArticles] = useState([]);
  const [interactiveArticles, setInteractiveArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featuredId, setFeaturedId] = useState('');
  const [secondaryId, setSecondaryId] = useState('');
  const [carouselOrder, setCarouselOrder] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAllArticles();
    loadSavedSelections();
  }, []);

  const fetchAllArticles = async () => {
    setLoading(true);
    try {
      // Fetch regular articles
      const articlesRes = await axios.get(`${API}/articles?limit=30`);
      const regularArticles = Array.isArray(articlesRes.data) 
        ? articlesRes.data 
        : articlesRes.data.articles || [];
      
      // Fetch interactive articles
      const interactiveRes = await axios.get(`${API}/interactive-articles?status=published&limit=30`);
      const cmsArticles = interactiveRes.data?.articles || [];
      
      setArticles(regularArticles);
      setInteractiveArticles(cmsArticles);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const loadSavedSelections = () => {
    const savedFeatured = localStorage.getItem('homepage_featured_story') || '';
    const savedSecondary = localStorage.getItem('homepage_secondary_story') || '';
    const savedCarousel = JSON.parse(localStorage.getItem('homepage_carousel_order') || '[]');
    
    setFeaturedId(savedFeatured);
    setSecondaryId(savedSecondary);
    setCarouselOrder(savedCarousel);
  };

  const handleSave = () => {
    setSaving(true);
    try {
      localStorage.setItem('homepage_featured_story', featuredId);
      localStorage.setItem('homepage_secondary_story', secondaryId);
      localStorage.setItem('homepage_carousel_order', JSON.stringify(carouselOrder));
      toast.success('Homepage layout saved! Refresh homepage to see changes.');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    localStorage.removeItem('homepage_featured_story');
    localStorage.removeItem('homepage_secondary_story');
    localStorage.removeItem('homepage_carousel_order');
    setFeaturedId('');
    setSecondaryId('');
    setCarouselOrder([]);
    toast.info('Reset to automatic selection (newest articles)');
  };

  // Combine all articles for selection
  const allArticles = [
    ...interactiveArticles.map(a => ({
      id: a.article_id,
      title: a.title,
      category: a.category || 'Interactive',
      image: a.featured_image,
      source: 'CMS'
    })),
    ...articles.map(a => ({
      id: a.article_id,
      title: a.title,
      category: a.category || 'News',
      image: a.image_url,
      source: 'Regular'
    }))
  ];

  // Get available articles for carousel (exclude featured and secondary)
  const carouselArticles = allArticles.filter(a => 
    a.id !== featuredId && a.id !== secondaryId
  );

  const moveCarouselItem = (index, direction) => {
    const newOrder = [...carouselOrder];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    setCarouselOrder(newOrder);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 text-center">
        <RefreshCw className="h-8 w-8 animate-spin mx-auto text-[#007AFF]" />
        <p className="mt-4 text-slate-600">Loading articles...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6" data-testid="homepage-layout-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#00204E]">Homepage Layout</h2>
          <p className="text-slate-600">Control the featured story, secondary story, and carousel order</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset}>
            Reset to Auto
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#00204E]">
            {saving ? 'Saving...' : 'Save Layout'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Featured Story Selection */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />
            <h3 className="font-bold text-[#00204E]">Featured Story (Large)</h3>
          </div>
          <p className="text-sm text-slate-500 mb-3">Select the main featured article with large image</p>
          
          <select
            value={featuredId}
            onChange={(e) => setFeaturedId(e.target.value)}
            className="w-full p-3 border rounded-lg text-sm"
            data-testid="featured-story-select"
          >
            <option value="">Auto (Newest Article)</option>
            {allArticles.map(article => (
              <option key={article.id} value={article.id}>
                [{article.source}] {article.title?.slice(0, 60)}...
              </option>
            ))}
          </select>
          
          {featuredId && (
            <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-800">
                Selected: {allArticles.find(a => a.id === featuredId)?.title?.slice(0, 50)}...
              </p>
            </div>
          )}
        </div>

        {/* Secondary Story Selection */}
        <div className="border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5 text-blue-500" />
            <h3 className="font-bold text-[#00204E]">Secondary Story (Compact)</h3>
          </div>
          <p className="text-sm text-slate-500 mb-3">Select the horizontal card below featured</p>
          
          <select
            value={secondaryId}
            onChange={(e) => setSecondaryId(e.target.value)}
            className="w-full p-3 border rounded-lg text-sm"
            data-testid="secondary-story-select"
          >
            <option value="">Auto (Second Newest)</option>
            {allArticles.filter(a => a.id !== featuredId).map(article => (
              <option key={article.id} value={article.id}>
                [{article.source}] {article.title?.slice(0, 60)}...
              </option>
            ))}
          </select>
          
          {secondaryId && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-800">
                Selected: {allArticles.find(a => a.id === secondaryId)?.title?.slice(0, 50)}...
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Carousel Order */}
      <div className="mt-6 border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <LayoutGrid className="h-5 w-5 text-purple-500" />
          <h3 className="font-bold text-[#00204E]">Top Stories Carousel Order</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Drag to reorder carousel stories. Unselected articles will use default order (newest first).
        </p>

        <div className="space-y-2 max-h-80 overflow-y-auto">
          {carouselArticles.slice(0, 10).map((article, index) => {
            const isInOrder = carouselOrder.includes(article.id);
            return (
              <div 
                key={article.id}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  isInOrder ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />
                <span className="text-xs font-bold text-gray-500 w-6">{index + 1}</span>
                {article.image && (
                  <img src={article.image} alt="" className="w-12 h-8 object-cover rounded" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{article.title}</p>
                  <p className="text-xs text-gray-500">{article.category} • {article.source}</p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => {
                      if (!carouselOrder.includes(article.id)) {
                        setCarouselOrder([...carouselOrder, article.id]);
                      } else {
                        setCarouselOrder(carouselOrder.filter(id => id !== article.id));
                      }
                    }}
                    className={`px-2 py-1 text-xs rounded ${
                      isInOrder 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    {isInOrder ? 'Pinned' : 'Pin'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-400 mt-3">
          Pinned articles maintain their order. Unpinned articles are sorted by publish date.
        </p>
      </div>

      {/* Preview Info */}
      <div className="mt-6 bg-slate-50 rounded-lg p-4">
        <h4 className="font-bold text-slate-700 mb-2">Layout Preview</h4>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="bg-yellow-100 p-2 rounded text-center">
            <Star className="h-4 w-4 mx-auto text-yellow-600 mb-1" />
            Featured (Large)
          </div>
          <div className="bg-blue-100 p-2 rounded text-center">
            <FileText className="h-4 w-4 mx-auto text-blue-600 mb-1" />
            Secondary (Compact)
          </div>
          <div className="bg-purple-100 p-2 rounded text-center">
            <LayoutGrid className="h-4 w-4 mx-auto text-purple-600 mb-1" />
            Carousel (Swipe)
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Changes take effect immediately after saving. Refresh the homepage to see updates.
        </p>
      </div>
    </div>
  );
};

export default AdminPage;